import type { ChatHistoryState, ChatState } from './interfaces';

export function chatHistoryReducer(state: ChatHistoryState.State, action: ChatState.Action): ChatHistoryState.State {
  return {
    manager: chatHistoryManagerReducer(state.manager, action),
    items: chatHistoryItemsReducer(state.items, action),
    shared: chatHistorySharedReducer(state.shared, action),
    tabs: chatHistoryTabsReducer(state.tabs, action),
  };
}

export function chatHistoryManagerReducer(state: ChatHistoryState.State['manager'], action: ChatState.Action): ChatHistoryState.State['manager'] {
  switch (action.type) {
    case 'instance-context-changed':
      return {
        ...state,
        cursor: null,
        hasMore: false,
        initialized: false,
      };

    case 'chat-history-loaded':
    case 'chat-history-page-loaded':
      return {
        ...state,
        cursor: action.payload.cursor,
        hasMore: action.payload.hasMore,
        loading: false,
        initialized: true,
      };

    case 'chat-history-mode-changed':
      return {
        ...state,
        cursor: null,
        hasMore: false,
        initialized: false,
        mode: action.payload.mode,
      };

    case 'chat-loading-started':
      return {
        ...state,
        loading: true,
        initialized: false,
      };

    case 'chat-loading-errored':
      return {
        ...state,
        loading: false,
        initialized: false,
      };
    case 'chat-brand-changed':
      return {
        ...state,
      };

    default:
      return state;
  }
}

const historyResetActions: Set<string> = new Set(['instance-context-changed', 'chat-history-mode-changed', 'chat-brand-changed']);

export function chatHistoryItemsReducer(state: ChatHistoryState.State['items'], action: ChatState.Action): ChatHistoryState.State['items'] {

  if (historyResetActions.has(action.type)) return [];

  switch (action.type) {
    case 'chat-history-loaded':
      return action.payload.items;

    case 'chat-history-page-loaded':
      return state.concat(action.payload.items);

    case 'chat-instance-removed':
      return state.filter(h => h.id !== action.payload.chatInstance.id);

    case 'chat-instance-renamed':
      return updateItemById(state, action.payload.chatInstance.id, { name: action.payload.chatInstance.name });

    case 'chat-instance-bookmarked':
      return updateItemById(state, action.payload.chatInstance.id, { bookmarked: action.payload.chatInstance.bookmarked });

    case 'new-chat-added':
      return state.concat(action.payload.chatInstance);

    case 'kol-search-submitted':
    case 'query-submitted':
      return handleQueryOrKolSubmitted(state, action.payload);

    case 'kol-search-results-available':
    case 'kol-search-profile-available':
      return handleKolResultsAvailable(state, action.payload);

    case 'query-response-available':
      return updateItemByIdentifier(state, action.payload.chatInstance.identifier, action.payload.chatInstance);

    case 'chat-loaded':
      return handleChatLoaded(state, action.payload);

    default:
      return state;
  }

}

function handleChatLoaded(state: ChatHistoryState.State['items'], payload: { chatInstance: { id: unknown; shared: unknown; identifier?: string } }): ChatHistoryState.State['items'] {
  if (payload.chatInstance.shared) return state;
  const existing = state.find(h => h.id === payload.chatInstance.id);
  if (existing) return state;
  return state.concat(payload.chatInstance as ChatHistoryState.State['items'][0]);
}

function updateItemById<T extends { id: unknown }>(items: T[], id: unknown, updates: Partial<T>): T[] {
  return items.map(x => x.id === id ? { ...x, ...updates } : x);
}

function updateItemByIdentifier<T extends { identifier: string }>(items: T[], identifier: string, updates: Partial<T>): T[] {
  return items.map(item => item.identifier === identifier ? { ...item, ...updates } : item);
}

function handleQueryOrKolSubmitted(state: ChatHistoryState.State['items'], payload: { chatIdentifier: string; brandIdentifier?: string }) {
  const existing = state.find(h => h.identifier === payload.chatIdentifier);
  if (existing) return state;
  return [
    {
      id: null,
      identifier: payload.chatIdentifier,
      name: 'Placeholder',
      createdOn: new Date(),
      currentQueryIdentifier: null,
      context: null,
      shared: null,
      bookmarked: false,
      filterOverrides: {},
      brandIdentifier: payload.brandIdentifier,
    },
    ...state,
  ];
}

function handleKolResultsAvailable(state: ChatHistoryState.State['items'], payload: { chatInstance: { identifier: string } }) {
  const existing = state.find(h => h.identifier === payload.chatInstance.identifier);
  if (!existing) {
    return [payload.chatInstance as ChatHistoryState.State['items'][0], ...state];
  }
  return state.map(item =>
    item.identifier === payload.chatInstance.identifier ? { ...item, ...payload.chatInstance } : item,
  );
}

export function chatHistorySharedReducer(state: ChatHistoryState.State['shared'], action: ChatState.Action): ChatHistoryState.State['shared'] {
  switch (action.type) {
    case 'instance-context-changed':
    case 'chat-history-mode-changed':
      return [];

    case 'chat-brand-changed':
      return [];

    case 'chat-history-loaded':
      return action.payload.shared;

    case 'chat-history-page-loaded':
      return state.concat(action.payload.shared);

    case 'chat-instance-removed':
      return state.filter(h => h.id !== action.payload.chatInstance.id);

    case 'chat-loaded': {
      if (!action.payload.chatInstance.shared) {
        return state;
      }

      const existing = state.find(h => h.id === action.payload.chatInstance.id);

      if (existing) {
        return state;
      }

      return state.concat(action.payload.chatInstance);
    }

    case 'chat-instance-bookmarked': {
      return state.reduce((acc, x) => {
        if (x.id === action.payload.chatInstance.id) {
          return acc.concat({
            ...x,
            bookmarked: action.payload.chatInstance.bookmarked,
          });
        }

        return acc.concat(x);
      }, []);
    }

    default:
      return state;
  }
}

export function chatHistoryTabsReducer(state: ChatHistoryState.State['tabs'], action: ChatState.Action): ChatHistoryState.State['tabs'] {
  switch (action.type) {
    case 'chat-tabs-index-changed':
      return {
        ...state,
        index: action.payload.index,
      };

    case 'chat-tabs-items-changed': {
      const newItems = action.payload.items;
      const clampedIndex =
        newItems.length === 0
          ? 0
          : Math.min(state.index, newItems.length - 1);

      return {
        ...state,
        items: newItems,
        index: clampedIndex,
      };
    }

    case 'chat-instance-removed': {
      return handleTabInstanceRemoved(state, action.payload.chatInstance.id);
    }

    case 'chat-loaded': {
      if (state.items.includes(action.payload.chatInstance.id)) {
        return state;
      }

      const newItems = state.items.concat(action.payload.chatInstance.id);
      const index = newItems.findIndex(id => id === action.payload.chatInstance.id);

      return {
        index,
        items: newItems,
      };
    }

    default:
      return state;
  }
}

function handleTabInstanceRemoved(state: ChatHistoryState.State['tabs'], removedId: unknown): ChatHistoryState.State['tabs'] {
  const oldItems = state.items;
  const removedIndex = oldItems.findIndex(id => id === removedId);
  const newItems = oldItems.filter(id => id !== removedId);

  let newIndex = state.index;

  if (removedIndex !== -1) {
    if (newItems.length === 0) {
      newIndex = 0;
    } else if (removedIndex < newItems.length) {
      newIndex = removedIndex;
    } else {
      newIndex = newItems.length - 1;
    }
  } else if (newIndex >= newItems.length) {
    newIndex = newItems.length === 0 ? 0 : newItems.length - 1;
  }

  return {
    ...state,
    items: newItems,
    index: newIndex,
  };
}
